import { useSearch } from '@tanstack/react-router';
import type { MailboxConnectionResponse } from '@orion/contracts';
import { Button, Card, ConfirmDialog } from '@orion/ui';
import { useState } from 'react';
import { PageHeader } from '../../app/shell.js';
import { mailboxConnectUrl, useDisconnectMailbox, useMailboxConnectionsQuery } from './mailbox-api.js';

const ERROR_MESSAGE: Record<string, string> = {
  not_configured: "This workspace isn't set up for mailbox connections yet — ask an admin to add the OAuth credentials.",
  state_mismatch: 'That connection attempt expired or was tampered with. Try connecting again.',
  connect_failed: "Something went wrong finishing the connection. If this keeps happening, try removing Orion's access from your Google/Microsoft account settings first, then reconnect.",
};

const PROVIDER_LABEL: Record<string, string> = { google: 'Google / Gmail', microsoft: 'Microsoft 365 / Outlook' };

/** Account ▸ Mailbox — self-service, same reasoning AccountSecurityPage's
 * MFA enrollment is self-service: the OAuth consent has to come from the
 * mailbox owner, so there's no admin screen for connecting someone else's
 * account. Connecting lets a reply sent from inside Orion go out from this
 * exact address — not a feature of its own yet (just the connection), see
 * the page's own copy below. */
export function AccountMailboxPage() {
  const search = useSearch({ from: '/_app/account/mailbox' }) as { connected?: string; error?: string };
  const { data: connections, isLoading } = useMailboxConnectionsQuery();
  const disconnect = useDisconnectMailbox();
  const [pendingDisconnect, setPendingDisconnect] = useState<MailboxConnectionResponse | null>(null);

  const connectedProviders = new Set((connections ?? []).map((c) => c.provider));

  return (
    <>
      <PageHeader title="Mailbox" />
      <main className="mx-auto flex max-w-[560px] flex-col gap-5 p-7">
        <Card className="p-6">
          <h2 className="text-[15px] font-semibold text-ink">Reply from your own mailbox</h2>
          <p className="mt-1 text-sm text-text-muted">
            Connect your Gmail or Microsoft 365 account so a reply you send from inside Orion goes out from your real address — the same one a lead's outreach came from — instead of a generic system sender.
            Orion only asks for permission to send; it never reads your inbox.
          </p>

          {search.error && (
            <div role="alert" className="mt-4 rounded-control bg-danger-soft px-3 py-2 text-sm font-medium text-danger">
              {ERROR_MESSAGE[search.error] ?? "Something went wrong connecting your mailbox."}
            </div>
          )}
          {search.connected && !search.error && (
            <div role="status" className="mt-4 rounded-control bg-success-soft px-3 py-2 text-sm font-medium text-success">
              {PROVIDER_LABEL[search.connected] ?? search.connected} connected.
            </div>
          )}

          {isLoading ? (
            <p className="mt-4 text-sm text-text-muted">Loading…</p>
          ) : (
            <div className="mt-4 flex flex-col gap-2">
              {(connections ?? []).map((c) => (
                <div key={c.id} className="flex items-center justify-between rounded-control bg-ground px-4 py-3">
                  <div className="flex flex-col gap-0.5">
                    <span className="text-sm font-medium text-ink">{PROVIDER_LABEL[c.provider] ?? c.provider}</span>
                    <span className="text-xs text-text-muted">{c.emailAddress}</span>
                    {c.status !== 'active' && <span className="text-xs font-medium text-danger">Needs reconnecting</span>}
                  </div>
                  <Button variant="secondary" onClick={() => setPendingDisconnect(c)}>
                    Disconnect
                  </Button>
                </div>
              ))}

              {!connectedProviders.has('google') && (
                <a href={mailboxConnectUrl('google')} className="flex items-center justify-between rounded-control border border-border-strong px-4 py-3 text-sm font-medium text-ink hover:bg-surface-subtle">
                  Connect Google / Gmail
                  <span className="text-text-muted">&rarr;</span>
                </a>
              )}
              {!connectedProviders.has('microsoft') && (
                <a href={mailboxConnectUrl('microsoft')} className="flex items-center justify-between rounded-control border border-border-strong px-4 py-3 text-sm font-medium text-ink hover:bg-surface-subtle">
                  Connect Microsoft 365 / Outlook
                  <span className="text-text-muted">&rarr;</span>
                </a>
              )}
            </div>
          )}
        </Card>
      </main>

      {pendingDisconnect && (
        <ConfirmDialog
          open
          title={`Disconnect ${PROVIDER_LABEL[pendingDisconnect.provider] ?? pendingDisconnect.provider}?`}
          description="Replies from inside Orion will stop being able to send through this mailbox until you reconnect it."
          confirmLabel="Disconnect"
          variant="danger"
          loading={disconnect.isPending}
          onConfirm={async () => {
            await disconnect.mutateAsync(pendingDisconnect.id);
            setPendingDisconnect(null);
          }}
          onClose={() => setPendingDisconnect(null)}
        />
      )}
    </>
  );
}
